import { useEffect, useRef, useState, useCallback } from "react";
import { Download, FileText, Image as ImageIcon, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PAGE_PX } from "@/lib/cvUtils";

const A4_W = 794; // px at 96dpi

export default function CvPreview({ cv, onLayout, onTemplate, templates = [] }) {
  const iframeRef = useRef(null);
  const wrapRef = useRef(null);
  const timer = useRef(null);
  const [html, setHtml] = useState("");
  const [fits, setFits] = useState(true);
  const [ifH, setIfH] = useState(PAGE_PX);
  const [displayScale, setDisplayScale] = useState(1);
  const [dl, setDl] = useState(null);

  // content signature: re-render preview when CONTENT/template change (not layout tweaks)
  const sig = JSON.stringify({
    p: cv.personal, s: cv.summary, e: cv.experience, ed: cv.education, sk: cv.skill_groups,
    c: cv.certifications, pr: cv.projects, a: cv.achievements, cs: cv.custom_sections,
    o: cv.section_order, t: cv.template,
  });

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const r = await api.cvRender({ ...cv, layout_settings: { sp: 1, fs: 1 } });
        setHtml(r.html);
      } catch { /* keep last preview */ }
    }, 450);
    return () => timer.current && clearTimeout(timer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig]);

  const fit = useCallback(() => {
    const ifr = iframeRef.current;
    const doc = ifr?.contentDocument;
    if (!doc || !doc.body) return;
    const root = doc.documentElement;
    let sp = 1, fs = 1;
    const setv = () => { root.style.setProperty("--sp", sp); root.style.setProperty("--fs", fs); };
    setv();
    const over = () => doc.body.scrollHeight > PAGE_PX + 6;
    let g = 0;
    while (over() && sp > 0.55 && g < 40) { sp = Math.round((sp - 0.05) * 100) / 100; setv(); g++; }
    g = 0;
    while (over() && fs > 0.8 && g < 20) { fs = Math.round((fs - 0.03) * 100) / 100; setv(); g++; }
    const ok = !over();
    setFits(ok);
    setIfH(Math.max(PAGE_PX, doc.body.scrollHeight));
    onLayout?.(sp, fs);
  }, [onLayout]);

  // display zoom to fit the preview column width
  useEffect(() => {
    const measure = () => { const w = wrapRef.current?.clientWidth || A4_W; setDisplayScale(Math.min(1, w / A4_W)); };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  const download = async (kind) => {
    setDl(kind);
    try {
      const res = await api.cvExport(cv, kind);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      const name = (cv.personal?.full_name || "cv").replace(/[^a-z0-9 _-]/gi, "").trim().replace(/\s+/g, "_") || "cv";
      a.href = url; a.download = `${name}.${kind}`; document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      const spill = res.headers["x-cv-fits"] === "0";
      toast.success(`${kind.toUpperCase()} downloaded${spill ? " — heads up: content still exceeds one page" : ""}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Download failed. Please try again."); } finally { setDl(null); }
  };

  return (
    <div className="flex h-full flex-col">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Select value={cv.template} onValueChange={onTemplate}>
            <SelectTrigger className="h-9 w-40 border-white/10 bg-white/5 text-[13px] text-white" data-testid="cv-template-select"><SelectValue /></SelectTrigger>
            <SelectContent className="border-white/10 bg-[#111214] text-white">
              {templates.map((t) => <SelectItem key={t.slug} value={t.slug} data-testid={`cv-template-${t.slug}`}>{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
          {fits
            ? <span data-testid="cv-page-indicator" className="inline-flex items-center gap-1 rounded-full bg-emerald-400/15 px-2.5 py-1 text-[12px] font-bold text-emerald-300"><CheckCircle2 size={13} /> 1 Page</span>
            : <span data-testid="cv-page-indicator" className="inline-flex items-center gap-1 rounded-full bg-amber-400/15 px-2.5 py-1 text-[12px] font-bold text-amber-300"><AlertTriangle size={13} /> Exceeds 1 page — shorten content</span>}
        </div>
        <div className="flex gap-2">
          <button onClick={() => download("pdf")} disabled={!!dl} data-testid="cv-download-pdf" className="inline-flex items-center gap-1.5 rounded-md bg-emerald-500 px-3 py-2 text-[13px] font-bold text-black hover:bg-emerald-400 disabled:opacity-50">{dl === "pdf" ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />} PDF</button>
          <button onClick={() => download("jpg")} disabled={!!dl} data-testid="cv-download-jpg" className="inline-flex items-center gap-1.5 rounded-md border border-white/20 px-3 py-2 text-[13px] font-bold text-white hover:border-white/40 disabled:opacity-50">{dl === "jpg" ? <Loader2 size={14} className="animate-spin" /> : <ImageIcon size={14} />} JPG</button>
        </div>
      </div>
      <div ref={wrapRef} className="flex-1 overflow-auto rounded-xl bg-[#e9ecef] p-3" data-testid="cv-preview">
        <div style={{ height: ifH * displayScale, position: "relative" }}>
          <div style={{ transform: `scale(${displayScale})`, transformOrigin: "top left", width: A4_W, height: ifH }}>
            <iframe ref={iframeRef} title="cv-preview" srcDoc={html} onLoad={fit}
              style={{ width: A4_W, height: ifH, border: "none", background: "#fff", boxShadow: "0 4px 24px rgba(0,0,0,.18)" }} />
          </div>
        </div>
      </div>
    </div>
  );
}
